/**
 * HandVerdictBar - Three-tier keep/risky/mulligan verdict with its reasoning
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import type { HandEvaluation, HandVerdict } from '@/features/decks/lib/handSim';

// ============================================================================
// Types
// ============================================================================

export interface HandVerdictBarProps {
  evaluation: HandEvaluation;
}

const VERDICT_STYLES: Record<HandVerdict, { label: string; pill: string; text: string }> = {
  keep: { label: 'Keep', pill: 'bg-emerald-500/20 text-emerald-300', text: 'text-emerald-400' },
  risky: { label: 'Risky', pill: 'bg-amber-500/20 text-amber-300', text: 'text-amber-400' },
  mulligan: { label: 'Mulligan', pill: 'bg-rose-500/20 text-rose-300', text: 'text-rose-400' },
};

// ============================================================================
// Component
// ============================================================================

export default function HandVerdictBar({ evaluation }: HandVerdictBarProps): ReactElement {
  const style = VERDICT_STYLES[evaluation.verdict];

  const facts = [
    `${evaluation.landCount} land${evaluation.landCount === 1 ? '' : 's'}${
      evaluation.rockContribution > 0 ? ` (+${evaluation.rockContribution.toFixed(2)} rocks)` : ''
    }`,
    `${evaluation.castableCount}/${evaluation.spellCount} castable now`,
    evaluation.firstPlayTurn !== null ? `first play T${evaluation.firstPlayTurn}` : 'no play',
  ];

  return (
    <div className="space-y-1 rounded-lg bg-zinc-800/40 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-silver truncate text-xs">{facts.join(' · ')}</span>
        <span className={clsx('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold', style.pill)}>{style.label}</span>
      </div>
      {evaluation.issues.length > 0 && (
        <ul className="space-y-0.5">
          {evaluation.issues.map((issue) => (
            <li
              key={issue.type}
              className={clsx('text-[11px]', issue.severity === 'critical' ? VERDICT_STYLES.mulligan.text : VERDICT_STYLES.risky.text)}
            >
              {issue.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
